import {
  queryOptions,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import type { SpiffReportMetadata } from "@/api/dto/process-instance";
import {
  buildCaseReportMetadata,
  type CaseListFilters,
} from "@/api/endpoints/build-case-report-metadata";
import { parseCaseReportMetadata } from "@/api/endpoints/parse-case-report-metadata";
import { CASE_CONFIG, type CaseConfig } from "@/domain/case/case-config";

/** 一覧の絞り込みに名前を付けて保存したもの（画面上は「ビュー」）。 */
export type SavedView = {
  readonly id: number;
  readonly name: string;
  readonly filters: CaseListFilters;
};

export type SavedViewPayload = {
  readonly identifier: string;
  readonly report_metadata: SpiffReportMetadata;
};

const QUERY_KEY = ["process-instances", "reports"] as const;

type SpiffReportRow = {
  readonly id: number;
  readonly identifier: string;
  readonly report_metadata: SpiffReportMetadata;
};

/**
 * 保存ビュー（SpiffArena の report）の一覧。
 *
 * サーバに置くことで端末・ブラウザを越えて同じビューが使える。
 */
export function savedViewsQueryOptions(client: ApiClient) {
  const fetchViews = async (): Promise<readonly SavedView[]> => {
    const response = await client.get<unknown>("/process-instances/reports");
    if (!Array.isArray(response)) return [];

    return (response as readonly SpiffReportRow[]).map((row) => ({
      id: row.id,
      name: row.identifier,
      filters: parseCaseReportMetadata(row.report_metadata),
    }));
  };

  return queryOptions({
    queryKey: QUERY_KEY,
    queryFn: fetchViews,
    staleTime: 60_000,
  });
}

/** 保存リクエストの body。一覧と同じ条件で引けるよう filter_by も入れる。 */
export function toSavedViewPayload(
  name: string,
  filters: CaseListFilters,
  config: CaseConfig = CASE_CONFIG,
): SavedViewPayload {
  return {
    identifier: name,
    report_metadata: buildCaseReportMetadata(filters, config),
  };
}

export function useSavedViews(): UseQueryResult<readonly SavedView[]> {
  return useQuery(savedViewsQueryOptions(useApiClient()));
}

export function useCreateSavedView(): UseMutationResult<
  unknown,
  Error,
  { readonly name: string; readonly filters: CaseListFilters }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, filters }) =>
      client.post(
        "/process-instances/reports",
        toSavedViewPayload(name, filters),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteSavedView(): UseMutationResult<
  unknown,
  Error,
  number
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => client.del(`/process-instances/reports/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
